/**
 * RegressionFacade — typed read surface over a regression-type
 * data-series source row. Obtained via `row.as(REGRESSION_FACADE)`.
 *
 * Unlike DataFacade/TasksFacade, regression depends on the CLOSEST
 * ANCESTOR with a `DATA_FACADE` attached — the regression curve
 * fits points from that ancestor's `DataIndex.peekSeries(seriesKey)`.
 * A regression row may sit several wrapper rows (block, group,
 * list, etc.) below its actual data source.
 *
 * Recomputes lazily on `seriesKey` / `regression` access. Dirty
 * is set by any of:
 *   - `ColumnDataChanged(row.id | boundAncestorId)`
 *   - `RowDataChanged(row.id)` (attributes/lookupKey change)
 *   - structural changes (`SheetStructureChanged`, `NewRowInserted`,
 *     `RowsReordered`) — these may shift the closest-ancestor walk
 *     so we conservatively recompute.
 *
 * On refresh we re-walk the ancestor chain to pick up reparenting
 * + intermediate-row insertions.
 */

import type { Option } from "../core/option.ts";
import { SheetId } from "../ids/sheet-id.ts";
import { Json } from "../core/json.ts";
import { FacadeKey, type FacadeFactory } from "./facade.ts";
import { DATA_FACADE, DataFacade } from "./data-facade.ts";
import {
  RegressionAttributes,
  RegressionIndex,
} from "../indices/regression-index.ts";
import type { KernelSmoothResult } from "../indices/regression-index.ts";
import {
  ColumnDataChanged,
  type Invalidation,
  NewRowInserted,
  RowDataChanged,
  RowsReordered,
  SheetStructureChanged,
} from "../tx/invalidation.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import { RowNetworkKey } from "../ids/row-network-key.ts";
import { SourceRow } from "../rows/variants/source-row.ts";
import { deepFrozen } from "../core/frozen-collections.ts";
import { Internals } from "../core/internals.ts";

/** The facade key that reaches a source row's `RegressionFacade`. */
export const REGRESSION_FACADE = new FacadeKey<RegressionFacade>("regression");

/** `RegressionFacade`'s friend record (`Internals`): its constructor registers each instance. */
type RegressionFacadeInternals = {
  readonly dispose: (facade: RegressionFacade) => void;
};
const regressionFacadeInternals = new Internals<RegressionFacade, RegressionFacadeInternals>("RegressionFacade");

/**
 * Tear down `facade`'s store subscription. The SDK-private door onto the
 * private `dispose` — a facade is cached per row and shared by every reader
 * of it (`row.as(key)`), so a consumer must not be able to stop it under the
 * others. Not on a barrel.
 */
export function disposeRegressionFacade(facade: RegressionFacade): void {
  regressionFacadeInternals.of(facade).dispose(facade);
}

/**
 * A smoothed regression over one of a source row's series, for a chart's
 * trend line.
 */
export class RegressionFacade {
  static readonly #internals: RegressionFacadeInternals = {
    dispose: (facade) => facade.#dispose(),
  };

  readonly #index = new RegressionIndex();
  #dirty = true;
  #boundAncestorId: Option<RowLocalId> = null;
  readonly #unsubscribe: () => void;

  readonly #row: SourceRow;

  constructor(row: SourceRow) {
    regressionFacadeInternals.register(this, RegressionFacade.#internals);
    this.#row = row;
    this.#unsubscribe = row.sheetReads.invalidationSink.subscribe(
      (e: Invalidation) => {
        if (e instanceof ColumnDataChanged) {
          if (e.rowId === row.id || e.rowId === this.#boundAncestorId) {
            this.#dirty = true;
          }
        } else if (e instanceof RowDataChanged && e.rowId === row.id) {
          this.#dirty = true;
        } else if (
          (e instanceof SheetStructureChanged && e.concerns(SheetId.parse(row.sheetReads.networkKey))) ||
          e instanceof NewRowInserted ||
          e instanceof RowsReordered
        ) {
          // Any structural change (in THIS sheet) can shift the
          // closest-ancestor walk.
          this.#dirty = true;
        }
      },
    );
  }

  /** Stop listening to the row's store. Reached through `disposeRegressionFacade`. */
  #dispose(): void {
    this.#unsubscribe();
  }

  // ── Queries (trigger lazy rebuild) ────────────────────────────────

  get seriesKey(): Option<string> {
    this.#refreshIfDirty();
    return this.#index.seriesKey;
  }

  get regression(): Option<KernelSmoothResult> {
    this.#refreshIfDirty();
    return this.#index.regression;
  }

  // ── Rebuild ────────────────────────────────────────────────────────

  #refreshIfDirty(): void {
    if (!this.#dirty) return;
    const found = findAncestorDataFacade(this.#row);
    this.#boundAncestorId = found?.ancestor.id ?? null;
    const attrs = this.#row.attributesAs(RegressionAttributes.decode);
    this.#index.populate(attrs, found?.facade ?? null);
    this.#dirty = false;
  }

  /**
   * Debug snapshot. Useful for CLI-based graph inspection and for
   * Tracing.log consumers that want to see the regression state
   * without traversing private fields.
   */
  debugSnapshot(): {
    seriesKey: Option<string>;
    boundAncestorId: Option<RowNetworkKey>;
    hasRegression: boolean;
    curvePointCount: number;
  } {
    this.#refreshIfDirty();
    return {
      seriesKey: this.#index.seriesKey,
      boundAncestorId: this.#boundAncestorId == null ? null : RowNetworkKey.of(this.#boundAncestorId),
      hasRegression: this.#index.regression != null,
      curvePointCount: this.#index.regression?.curve.length ?? 0,
    };
  }
}

/**
 * Find the closest in-scope `DATA_FACADE` for the regression row.
 * The data-series row is usually a sibling of the data-json source
 * row (both children of the data alias), so DATA_FACADE doesn't
 * live on an ancestor — it lives on a sibling.
 *
 * Walk: self → parent → check parent's source children for
 * `DATA_FACADE` → if none match, recurse upward.
 *
 * Returns the source row + facade that won, or null if nothing
 * with DATA_FACADE exists in scope.
 */
function findAncestorDataFacade(
  row: SourceRow,
): Option<{ facade: DataFacade; ancestor: SourceRow }> {
  let current = row.parent;
  while (current) {
    for (const child of current.children) {
      if (child === row) continue;
      if (!(child instanceof SourceRow)) continue;
      const facade = child.as(DATA_FACADE);
      if (facade) return { facade, ancestor: child };
    }
    current = current.parent;
  }
  return null;
}

/** Values that go with `RegressionFacade`: `factory`. */
export namespace RegressionFacade {
  /**
   * Build a facade for every regression-type data-series row. The
   * row's `descriptor.id` is the slot id (e.g. `column.series-summary`);
   * the regression gate keys off `attributes.seriesType === "regression"`.
   */
  export const factory: FacadeFactory<RegressionFacade> = deepFrozen({
    key: REGRESSION_FACADE,
    build(row: SourceRow): Option<RegressionFacade> {
      if (Json.string(row.attributes(), "seriesType") !== "regression") return null;
      return new RegressionFacade(row);
    },
  });
}
